import { definePreset, palette } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

const primaryPalette = palette('#971B49');

const AltorneyPreset = definePreset(Lara, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '3px',
      md: '4px',
      lg: '6px',
      xl: '8px',
    },
    borderWidth: {
      none: '0',
      xs: '0.5px',
      sm: '1px',
      md: '2px',
      lg: '3px',
      xl: '4px',
    },
  },

  semantic: {
    // ── Primary brand color ──────────────────────────────────────
    primary: primaryPalette,

    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },

        // ── Surface: Bootstrap gray palette ────────────────────────
        surface: {
          0: '#ffffff',
          50: '#f8f9fa',
          100: '#f1f5f9',
          200: '#e9ecef',
          300: '#dee2e6',
          400: '#ced4da',
          500: '#adb5bd',
          600: '#6c757d',
          700: '#495057',
          800: '#343a40',
          900: '#212529',
          950: '#121212',
        },

        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.500}',
          focusColor: '{primary.600}',
        },

        list: {
          option: {
            focusBackground: '{surface.200}',
            padding: '0.5rem 1rem',
          },
        },
      },
    },

    // ── Typography ─────────────────────────────────────────────────
    fontFamily: '"HK Grotesk", sans-serif',
    fontSize: '1rem',
    fontWeight: {
      light: '400',
      normal: '500',
      medium: '600',
      semibold: '600',
      bold: '700',
    },

    // ── Transitions ────────────────────────────────────────────────
    transitionDuration: '0.2s',

    // ── Disabled state ─────────────────────────────────────────────
    disabledOpacity: '0.8',

    // ── Focus ring ─────────────────────────────────────────────────
    focusRing: {
      width: '1.5px',
      style: 'solid',
      color: 'rgba(139, 26, 58, 1)',
      offset: '0',
    },

    // ── Form fields ────────────────────────────────────────────────
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.5rem',
        paddingY: '0.375rem',
      },
      borderRadius: '1rem',
      color: '#333333',
      focusRing: {
        width: '1px',
        style: 'solid',
        color: 'rgba(139, 26, 58, 1)',
        offset: '0',
        shadow: 'none',
      },
    },

    // ── Content ────────────────────────────────────────────────────
    content: {
      borderRadius: '{border.radius.sm}',
    },

    // ── Overlay ────────────────────────────────────────────────────
    overlay: {
      select: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 3px 6px 0 rgba(0, 0, 0, 0.1)',
      },
      popover: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 0 14px 0 rgba(0, 0, 0, 0.1)',
      },
      modal: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 0 14px 0 rgba(0, 0, 0, 0.1)',
      },
    },

    // ── Navigation (menu) ──────────────────────────────────────────
    navigation: {
      item: {
        borderRadius: '0',
      },
    },

    // ── Mask ───────────────────────────────────────────────────────
    mask: {
      background: 'rgba(0, 0, 0, 0.4)',
    },
  },

  // ══════════════════════════════════════════════════════════════════
  // Component-level overrides
  // ══════════════════════════════════════════════════════════════════
  components: {
    // ── Button ────────────────────────────────────────────────────
    button: {
      root: {
        borderRadius: '5.5px',
        paddingY: '0.25rem',
        paddingX: '0.75rem',
        sm: {
          fontSize: '1em',
          paddingY: '0.25rem',
          paddingX: '0.75rem',
        },
        label: {
          fontWeight: '500',
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.500}',
              hoverBackground: '#BD5179',
              activeBackground: '#BD5179',
              borderColor: '{primary.500}',
              hoverBorderColor: '#BD5179',
              activeBorderColor: '#BD5179',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: 'rgba(139, 26, 58, 1)',
                shadow: 'none',
              },
            },
            secondary: {
              background: '#607D8B',
              hoverBackground: '#546E7A',
              activeBackground: '#455A64',
              borderColor: '#607D8B',
              hoverBorderColor: '#546E7A',
              activeBorderColor: '#455A64',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#B0BEC5',
                shadow: 'none',
              },
            },
            info: {
              background: '#03A9F4',
              hoverBackground: '#039BE5',
              activeBackground: '#0288D1',
              borderColor: '#03A9F4',
              hoverBorderColor: '#039BE5',
              activeBorderColor: '#0288D1',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
            success: {
              background: '#4CAF50',
              hoverBackground: '#43A047',
              activeBackground: '#388E3C',
              borderColor: '#4CAF50',
              hoverBorderColor: '#43A047',
              activeBorderColor: '#388E3C',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
            warn: {
              background: '#FFC107',
              hoverBackground: '#FFB300',
              activeBackground: '#FFA000',
              borderColor: '#FFC107',
              hoverBorderColor: '#FFB300',
              activeBorderColor: '#FFA000',
              color: '#495057',
              hoverColor: '#495057',
              activeColor: '#495057',
            },
            danger: {
              background: '#f44336',
              hoverBackground: '#e53935',
              activeBackground: '#d32f2f',
              borderColor: '#f44336',
              hoverBorderColor: '#e53935',
              activeBorderColor: '#d32f2f',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
            help: {
              background: '#9C27B0',
              hoverBackground: '#8E24AA',
              activeBackground: '#7B1FA2',
              borderColor: '#9C27B0',
              hoverBorderColor: '#8E24AA',
              activeBorderColor: '#7B1FA2',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
          },
        },
      },
    },

    // ── InputText / form inputs ──────────────────────────────────
    inputtext: {
      root: {
        background: '#ffffff',
        color: '#333333',
        placeholderColor: '#6c757d',
        borderColor: '#ced4da',
        hoverBorderColor: '{primary.500}',
        focusBorderColor: '{primary.500}',
        invalidBorderColor: '#e4677e',
      },
      css: `
        .p-inputtext {
          font-weight: 500;
        }
      `,
    },

    // ── Select (Dropdown) ────────────────────────────────────────
    select: {
      root: {
        background: '#ffffff',
        color: '#495057',
        borderColor: '#ced4da',
        hoverBorderColor: '{primary.500}',
        focusBorderColor: '{primary.500}',
        invalidBorderColor: '#e4677e',
      },
      list: {
        padding: '0.5rem',
      },
      css: `
        ul.p-select-list {
          padding: 0.5rem !important;
        }
      `,
    },

    // ── Checkbox ─────────────────────────────────────────────────
    checkbox: {
      root: {
        borderRadius: '0',
        borderColor: '#ced4da',
        checkedBackground: '{primary.500}',
        checkedBorderColor: '{primary.500}',
        checkedHoverBackground: '#BD5179',
        checkedHoverBorderColor: '#BD5179',
        disabledBackground: '#e9ecef',
        checkedDisabledBorderColor: '#ced4da',
      },
      icon: {
        disabledColor: '#adb5bd',
      },
    },

    // ── RadioButton ──────────────────────────────────────────────
    radiobutton: {
      root: {
        width: '20px',
        height: '20px',
        background: '#ffffff',
        borderColor: '{primary.500}',
        hoverBorderColor: '#BD5179',
        checkedBackground: '#ffffff',
        checkedBorderColor: '{primary.500}',
        checkedHoverBackground: '#ffffff',
        checkedHoverBorderColor: '#BD5179',
      },
      icon: {
        size: '12px',
        checkedColor: '{primary.500}',
        checkedHoverColor: '#BD5179',
      },
      css: `
        .p-radiobutton:not(.p-radiobutton-checked) .p-radiobutton-icon {
          display: none;
        }
      `,
    },

    // ── ToggleSwitch ─────────────────────────────────────────────
    toggleswitch: {
      root: {
        borderRadius: '30px',
      },
      colorScheme: {
        light: {
          root: {
            checkedBackground: '{primary.500}',
            checkedHoverBackground: '#BD5179',
          },
          handle: {
            checkedBackground: '#ffffff',
          },
        },
      },
    },

    // ── Slider ───────────────────────────────────────────────────
    slider: {
      track: {
        background: '#dee2e6',
      },
      range: {
        background: '{primary.500}',
      },
      handle: {
        background: '{primary.500}',
        content: {
          borderRadius: '50%',
        },
      },
      colorScheme: {
        light: {
          handle: {
            content: {
              background: '#ffffff',
            },
          },
        },
      },
    },

    // ── DataTable ────────────────────────────────────────────────
    datatable: {
      css: `
        ul.p-datatable-filter-constraint-list {
          padding: 0.5rem !important;
        }
      `,
      filter: {
        constraintList: {
          padding: '0.5rem',
        },
      },
      headerCell: {
        padding: '0.25rem 0.5rem',
        gap: '0.5rem',
      },
      columnTitle: {
        fontWeight: '600',
      },
      sortIcon: {
        color: '#8b1a3a',
        hoverColor: '{primary.500}',
        size: '1.25rem',
      },
      bodyCell: {
        padding: '0.4rem',
      },
      row: {
        stripedBackground: '#f8f9fa',
      },
      colorScheme: {
        light: {
          root: {
            borderColor: '#dee2e6',
          },
          header: {
            background: '#ffffff',
            color: '#495057',
            borderColor: '#dee2e6',
          },
          headerCell: {
            background: '#ffffff',
            color: '#8b1a3a',
            borderColor: '#dee2e6',
            hoverBackground: '#e9ecef',
            selectedBackground: '#ffffff',
            selectedColor: '#971b49',
          },
          row: {
            background: '#ffffff',
            color: '#495057',
            hoverBackground: '#e9ecef',
            selectedBackground: '{primary.50}',
            selectedColor: '{primary.500}',
          },
          bodyCell: {
            borderColor: '#dee2e6',
          },
          footer: {
            background: '#f8f9fa',
            color: '#495057',
            borderColor: '#e9ecef',
          },
          footerCell: {
            background: '#f8f9fa',
            color: '#495057',
            borderColor: '#e9ecef',
          },
          paginatorTop: {
            borderColor: '#e9ecef',
          },
          paginatorBottom: {
            borderColor: '#e9ecef',
          },
        },
      },
    },

    // ── Paginator ────────────────────────────────────────────────
    paginator: {
      root: {
        borderRadius: '0 0 1rem 1rem',
        padding: '0.5rem 1rem',
      },
      navButton: {
        color: '#495057',
        hoverColor: '{primary.500}',
        selectedBackground: '{primary.500}',
        selectedColor: '#ffffff',
      },
      currentPageReport: {
        color: '#495057',
      },
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            color: '#495057',
          },
        },
      },
    },

    // ── Panel ────────────────────────────────────────────────────
    panel: {
      root: {
        background: '#ffffff',
        color: '#495057',
      },
      header: {
        borderColor: '#dee2e6',
      },
      colorScheme: {
        light: {
          header: {
            background: '#f8f9fa',
            color: '#495057',
          },
        },
      },
    },

    // ── Accordion ────────────────────────────────────────────────
    accordion: {
      header: {
        color: '#495057',
        borderColor: '#dee2e6',
      },
      content: {
        background: '#ffffff',
        borderColor: '#dee2e6',
      },
      colorScheme: {
        light: {
          header: {
            background: '#f8f9fa',
            hoverBackground: '#e9ecef',
            activeBackground: '#f8f9fa',
            activeHoverBackground: '#e9ecef',
          },
        },
      },
    },

    // ── Tabs (formerly TabView) ──────────────────────────────────
    tabs: {
      tablist: {
        background: 'transparent',
        borderColor: '#dee2e6',
      },
      tab: {
        color: '#6c757d',
        activeColor: '{primary.color}',
        activeBorderColor: 'transparent',
        padding: '0',
      },
      colorScheme: {
        light: {
          tab: {
            background: 'transparent',
            hoverBackground: 'transparent',
            activeBackground: 'transparent',
          },
        },
      },
      css: `
        .p-tablist-content {
          margin-bottom: 1rem;
        }
      `,
    },

    // ── Dialog ───────────────────────────────────────────────────
    dialog: {
      root: {
        borderRadius: '1.5rem',
      },
      header: {
        padding: '1.5rem',
      },
      content: {
        padding: '0 1.5rem',
      },
      footer: {
        padding: '1.5rem',
      },
    },

    // ── Toast ────────────────────────────────────────────────────
    toast: {
      root: {
        borderWidth: '0 0 0 4px',
      },
      summary: {
        fontWeight: '600',
        fontSize: '1em',
      },
      detail: {
        fontWeight: '500',
        fontSize: '1em',
      },
      colorScheme: {
        light: {
          info: {
            background: '#ffffff',
            borderColor: '#039BE5',
            color: '#333333',
            detailColor: '#333333',
          },
          success: {
            background: '#ffffff',
            borderColor: '{primary.500}',
            color: '#333333',
            detailColor: '#333333',
          },
          warn: {
            background: '#ffffff',
            borderColor: '#FFB300',
            color: '#333333',
            detailColor: '#333333',
          },
          error: {
            background: '#ffffff',
            borderColor: '#E53935',
            color: '#333333',
            detailColor: '#333333',
          },
        },
      },
    },

    // ── Message ──────────────────────────────────────────────────
    message: {
      root: {
        borderWidth: '0 0 0 4px',
      },
      colorScheme: {
        light: {
          info: {
            background: '#039BE5',
            borderColor: '#027cb7',
            color: '#ffffff',
          },
          success: {
            background: '#43A047',
            borderColor: '#43A047',
            color: '#ffffff',
          },
          warn: {
            background: '#FFB300',
            borderColor: '#FFB300',
            color: '#495057',
          },
          error: {
            background: '#E53935',
            borderColor: '#E53935',
            color: '#ffffff',
          },
        },
      },
    },

    // ── Menu ─────────────────────────────────────────────────────
    menu: {
      root: {
        background: '#ffffff',
        color: '#495057',
        borderColor: '#dee2e6',
      },
      list: {
        padding: '0.5rem',
      },
      item: {
        color: '#495057',
        focusBackground: '#e5e7eb',
        focusColor: '#495057',
        padding: '0.5rem 1rem',
      },
    },

    // ── Tooltip ──────────────────────────────────────────────────
    tooltip: {
      root: {
        padding: '0.5rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '#495057',
            color: '#ffffff',
          },
        },
      },
    },

    // ── Badge ────────────────────────────────────────────────────
    badge: {
      colorScheme: {
        light: {
          primary: {
            background: '{primary.500}',
            color: '#ffffff',
          },
          danger: {
            background: '{primary.500}',
            color: '#ffffff',
          },
        },
      },
    },

    // ── Tag ──────────────────────────────────────────────────────
    tag: {
      root: {
        borderRadius: '9999px',
      },
    },

    // ── Chip ─────────────────────────────────────────────────────
    chip: {
      root: {
        borderRadius: '16px',
        background: '#dee2e6',
        color: '#495057',
      },
    },

    // ── ProgressBar ──────────────────────────────────────────────
    progressbar: {
      root: {
        background: '#dee2e6',
      },
      value: {
        background: '{primary.500}',
      },
    },

    // ── Card ─────────────────────────────────────────────────────
    card: {
      root: {
        background: '#ffffff',
        color: '#495057',
        borderRadius: '{border.radius.sm}',
        shadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
      },
    },

    // ── Divider ──────────────────────────────────────────────────
    divider: {
      root: {
        borderColor: '#dee2e6',
      },
    },

    // ── Skeleton ─────────────────────────────────────────────────
    skeleton: {
      colorScheme: {
        light: {
          root: {
            background: '#e9ecef',
            animationBackground: 'rgba(255, 255, 255, 0.4)',
          },
        },
      },
    },

    // ── Avatar ───────────────────────────────────────────────────
    avatar: {
      root: {
        background: '#dee2e6',
        color: '#495057',
      },
    },

    // ── Rating ───────────────────────────────────────────────────
    rating: {
      icon: {
        activeColor: '{primary.500}',
        color: '{text.muted.color}',
      },
    },

    // ── Stepper ──────────────────────────────────────────────────
    stepper: {
      stepNumber: {
        activeBackground: '{primary.500}',
        activeBorderColor: '{primary.500}',
        activeColor: '#ffffff',
      },
    },

    // ── Breadcrumb ───────────────────────────────────────────────
    breadcrumb: {
      root: {
        padding: '1rem',
        background: '#ffffff',
      },
    },

    // ── MultiSelect ──────────────────────────────────────────────
    multiselect: {
      root: {
        background: '#ffffff',
        color: '#495057',
        borderColor: '#ced4da',
        hoverBorderColor: '{primary.500}',
        focusBorderColor: '{primary.500}',
        invalidBorderColor: '#e4677e',
      },
      list: {
        padding: '0',
        header: {
          padding: '0.5rem 1rem',
        },
      },
      option: {
        selectedBackground: '{primary.500}',
        selectedColor: '{primary.contrastColor}',
        selectedFocusBackground: '{primary.500}',
        selectedFocusColor: '{primary.contrastColor}',
      },
    },

    // ── SelectButton ─────────────────────────────────────────────
    selectbutton: {
      root: {
        borderRadius: '{form.field.border.radius}',
      },
      colorScheme: {
        light: {
          root: {
            invalidBorderColor: '#e4677e',
          },
        },
      },
    },

    // ── ToggleButton ─────────────────────────────────────────────
    togglebutton: {
      root: {
        background: '#ffffff',
        borderColor: '#ced4da',
        color: '#495057',
        hoverColor: '#495057',
        checkedBackground: '{primary.50}',
        checkedColor: '{primary.500}',
        checkedBorderColor: '{primary.500}',
      },
      colorScheme: {
        light: {
          root: {
            hoverBackground: '{surface.100}',
          },
        },
      },
    },

    // ── FileUpload ───────────────────────────────────────────────
    fileupload: {
      root: {
        background: '#ffffff',
        borderColor: '#dee2e6',
        color: '#495057',
        borderRadius: '{content.border.radius}',
      },
      header: {
        background: '#f8f9fa',
        color: '#495057',
        borderColor: '#dee2e6',
      },
      content: {
        padding: '1.125rem',
      },
    },

    // ── InputGroup ───────────────────────────────────────────────
    inputgroup: {
      addon: {
        borderRadius: '{form.field.border.radius}',
      },
      colorScheme: {
        light: {
          addon: {
            background: '#f8f9fa',
            borderColor: '#ced4da',
            color: '#6c757d',
          },
        },
      },
    },

    // ── OrderList ────────────────────────────────────────────────
    orderlist: {
      root: {
        gap: '1.125rem',
      },
    },

    // ── PickList ─────────────────────────────────────────────────
    picklist: {
      root: {
        gap: '1.125rem',
      },
    },

    // ── SpeedDial ────────────────────────────────────────────────
    speeddial: {
      root: {
        gap: '0.5rem',
      },
    },

    // ── DatePicker ───────────────────────────────────────────────
    datepicker: {
      panel: {
        background: '#ffffff',
        borderColor: '#ced4da',
        color: '#495057',
      },
      colorScheme: {
        light: {
          dropdown: {
            background: '#f8f9fa',
            hoverBackground: '#e9ecef',
            activeBackground: '#dee2e6',
            color: '#6c757d',
            hoverColor: '#495057',
            activeColor: '#343a40',
          },
          today: {
            background: '#e9ecef',
            color: '#212529',
          },
        },
      },
    },
  },
});

export default AltorneyPreset;
